import type { DividendEvent } from "@/types/portfolio"

export interface IncomeSummary {
	thisMonth: number
	yearToDate: number
	lastYearTotal: number
	yoyGrowth: number | null
}

export function calculateIncomeSummary(
	events: DividendEvent[],
	convert?: (amount: number, fromCurrency?: string) => number,
): IncomeSummary {
	const now = new Date()
	const thisYear = now.getFullYear()
	const thisMonth = now.getMonth()
	const lastYear = thisYear - 1

	let thisMonthTotal = 0
	let yearToDate = 0
	let lastYearTotal = 0

	events.forEach((event) => {
		if (event.status !== "paid") return
		const amount = convert ? convert(event.amount, event.currency) : event.amount
		const payDate = new Date(event.payment_date)
		const eventYear = payDate.getFullYear()
		const eventMonth = payDate.getMonth()

		if (eventYear === thisYear && eventMonth === thisMonth) {
			thisMonthTotal += amount
		}
		if (eventYear === thisYear) {
			yearToDate += amount
		}
		if (eventYear === lastYear) {
			lastYearTotal += amount
		}
	})

	// YoY growth: compare this year's YTD against the same months last year
	let lastYearSameMonths = 0
	events.forEach((event) => {
		if (event.status !== "paid") return
		const amount = convert ? convert(event.amount, event.currency) : event.amount
		const payDate = new Date(event.payment_date)
		if (payDate.getFullYear() === lastYear && payDate.getMonth() <= thisMonth) {
			lastYearSameMonths += amount
		}
	})

	const yoyGrowth = lastYearSameMonths > 0 ? ((yearToDate - lastYearSameMonths) / lastYearSameMonths) * 100 : null

	return { thisMonth: thisMonthTotal, yearToDate, lastYearTotal, yoyGrowth }
}

export interface MonthlyAggregate {
	month: number // 0-11 (Jan-Dec)
	monthName: string
	paid_total: number
	declared_total: number
	predicted_total: number
	events: DividendEvent[]
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

export function aggregateDividendsByMonth(
	events: DividendEvent[],
	viewYear: number,
	convert?: (amount: number, fromCurrency?: string) => number,
): MonthlyAggregate[] {
	// Initialize 12 months with zero totals
	const months: MonthlyAggregate[] = Array.from({ length: 12 }, (_, i) => ({
		month: i,
		monthName: MONTH_NAMES[i],
		paid_total: 0,
		declared_total: 0,
		predicted_total: 0,
		events: [],
	}))

	// Filter events for the specified year and aggregate
	events.forEach((event) => {
		const paymentDate = new Date(event.payment_date)
		const eventYear = paymentDate.getFullYear()

		if (eventYear !== viewYear) return

		const monthIndex = paymentDate.getMonth()
		const monthData = months[monthIndex]

		const convertAmount = convert ? convert(event.amount, event.currency) : event.amount

		// Add to appropriate total based on status
		switch (event.status) {
			case "paid":
				monthData.paid_total += convertAmount
				break
			case "declared":
				monthData.declared_total += convertAmount
				break
			case "predicted":
				monthData.predicted_total += convertAmount
				break
		}

		// Add event to the month's events array
		monthData.events.push(event)
	})

	return months
}
